<template>
  <div v-if="showComponent" :class="$style.base" v-test="'waiting-list'">
    <div v-test="'waiting-list-explanation'">
      {{ waitingListExplanation }}
    </div>
    <ProForm
      v-if="waitingListProEnabled"
      :submitted="submitted"
      :partsAreInvalid="partsAreInvalid"
      :daysAreInvalid="daysAreInvalid"
      @select="onSelectSpecificDate"
      @back="emit('back')"
    />
    <BaseButton
      showArrow
      :class="$style.button"
      @click="submit"
      v-test="'btn-waiting-list'"
    >
      {{ $t('waiting_list.button_submit') }}
    </BaseButton>
  </div>
</template>

<script setup lang="ts">
import BaseButton from '@/components/BaseButton.vue';
import { useAvailabilityStore } from '@/stores/availability';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ProForm from './ProForm.vue';
const { t } = useI18n();

defineOptions({ name: 'WaitingList' });
const emit = defineEmits(['submit', 'back']);

const { waitingListProEnabled } = useCompanyStore();

const showComponent = computed(() => {
  const { date, employeeId } = useBookingStore();
  if (!date) {
    return false;
  }

  const { availabilitiesToday: today } = useAvailabilityStore();

  const availableTimeslots =
    today && employeeId
      ? today.timeslots.filter(
          (slot) => employeeId && slot.resource_ids.includes(employeeId)
        )
      : today?.timeslots || [];

  if (
    !today ||
    !today.open ||
    availableTimeslots.length > 0 ||
    (employeeId && !today.open_resources.includes(employeeId))
  ) {
    return false;
  }

  return true;
});

const waitingListExplanation = computed(() => {
  const { employee } = useBookingStore();
  if (waitingListProEnabled && employee) {
    return t('waiting_list.info', { employee: employee.name });
  }
  return t('waiting_list.explanation');
});

const waitingListOnSpecificDate = ref(true);
const onSelectSpecificDate = (stateValue: boolean) =>
  (waitingListOnSpecificDate.value = stateValue);

const { partsOfDay, daysOfWeek } = storeToRefs(useBookingStore());
const submitted = ref(false);
const partsAreInvalid = computed(
  () => !waitingListOnSpecificDate.value && !partsOfDay.value.length
);
const daysAreInvalid = computed(
  () => !waitingListOnSpecificDate.value && !daysOfWeek.value.length
);
const submit = () => {
  submitted.value = true;
  if (partsAreInvalid.value || daysAreInvalid.value) {
    return;
  }

  const bookingStore = useBookingStore();
  bookingStore.waitingList = true;

  emit('submit');
};
</script>

<style lang="scss" module>
.base {
  padding: $spacing $spacing 0;
  border-top: 1px solid $color-border;
  margin-bottom: $spacing;
}

.button {
  margin-top: $spacing * 2;
  width: 100%;
}
</style>
